import React, { useCallback } from "react";
import { CustomDropdownSelect, CustomDatePicker, CustomButton, FlexColumn, Row, TextLabel } from "../MetricDashboard.styles";
import { cfstDropDownFilterValues } from "../../../constants/DropdownConstants";
import { cfstConstants } from "./constants";
import PropTypes from "prop-types";

const FilterBlock = (props) => {
  const resetDates = useCallback(() => {
    return {
      fromDate: cfstConstants.defaultMMDDYYYY,
      toDate: cfstConstants.defaultMMDDYYYY,
      selectDates: { startDate: "", endDate: "" },
    };
  }, []);

  const handleDropdownChange = useCallback(
    (e) => {
      const selectedValue = e.target.value;
      props.setFilterResponse((prevState) => {
        return {
          ...prevState,
          ...resetDates(),
          cfstFilterValue: selectedValue,
        };
      });
    },
    [props, resetDates]
  );

  const handleFromDate = useCallback(
    (date) => {
      props.setFilterResponse((prevState) => {
        return {
          ...prevState,
          fromDate: date,
          cfstFilterValue: "", // Reset dropdown when date is selected
          selectDates: { ...prevState.selectDates, startDate: date },
        };
      });
    },
    [props]
  );

  const handleToDate = useCallback(
    (date) => {
      props.setFilterResponse((prevState) => {
        return {
          ...prevState,
          toDate: date,
          cfstFilterValue: "", // Reset dropdown when date is selected
          selectDates: { ...prevState.selectDates, endDate: date },
        };
      });
    },
    [props]
  );

  const handleApplyButton = useCallback(() => {
    props.getDashboardCountsWithDeRe();
  }, [props]);

  const handleResetButton = useCallback(() => {
    props.setFilterResponse((prevState) => {
      return {
        ...prevState,
        ...resetDates(),
        cfstFilterValue: cfstDropDownFilterValues[1].value, // Default to "last_5_days"
      };
    });
    props.setIsFilterReset(true);
  }, [props, resetDates]);

  const shouldDisableApplyButton = useCallback(() => {
    const { fromDate, toDate, cfstFilterValue } = props.filterResponse;

    // Disable if no filter option is selected and no valid date range is provided
    if (!cfstFilterValue && (fromDate === cfstConstants.defaultMMDDYYYY || toDate === cfstConstants.defaultMMDDYYYY)) {
      return true;
    }

    // Disable if only one date is selected (partial date range)
    return (
      (fromDate === cfstConstants.defaultMMDDYYYY && toDate !== cfstConstants.defaultMMDDYYYY) ||
      (fromDate !== cfstConstants.defaultMMDDYYYY && toDate === cfstConstants.defaultMMDDYYYY)
    );
  }, [props.filterResponse]);

  const shouldDisableResetButton = useCallback(() => {
    const { fromDate, toDate, cfstFilterValue } = props.filterResponse;
    return (cfstFilterValue === cfstDropDownFilterValues[1].value && (fromDate === cfstConstants.defaultMMDDYYYY || toDate === cfstConstants.defaultMMDDYYYY))
  }, [props.filterResponse]);

  const getDateLimit = useCallback((actualDate) => {
    const dateString = actualDate.toString(); // MM/DD/YYYY format
    const dateObject = new Date(dateString);

    const year = dateObject.getFullYear();
    const month = dateObject.getMonth() + 1;
    const day = dateObject.getDate();

    return `${year}, ${month}, ${day}`;
  }, []);

  return (
    <Row style={{ marginTop: "40px" }}>
      <FlexColumn>
        <TextLabel>Filter For</TextLabel>
        <CustomDropdownSelect onChange={handleDropdownChange} value={props.filterResponse.cfstFilterValue || ""}>
          {cfstDropDownFilterValues.map((item) => {
            return (
              <option key={item.id} value={item.value}>
                {item.label}
              </option>
            );
          })}
        </CustomDropdownSelect>
      </FlexColumn>
      <span style={{ fontWeight: "700" }}>(or)</span>
      <FlexColumn>
        <TextLabel>From Date</TextLabel>
        <CustomDatePicker
          dateFormat={cfstConstants.defaultMMDDYYYY}
          surface="light"
          value={props.filterResponse.fromDate}
          onChange={handleFromDate}
          width="100%"
          minDate={new Date(1998, 1, 1)}
          maxDate={props.filterResponse.toDate !== cfstConstants.defaultMMDDYYYY ? new Date(getDateLimit(props.filterResponse.toDate)) : new Date()}
        />
      </FlexColumn>
      <FlexColumn>
        <TextLabel>To Date</TextLabel>
        <CustomDatePicker
          dateFormat={cfstConstants.defaultMMDDYYYY}
          surface="light"
          minDate={
            props.filterResponse.fromDate !== cfstConstants.defaultMMDDYYYY
              ? new Date(getDateLimit(props.filterResponse.fromDate))
              : new Date(1998, 1, 1)
          }
          value={props.filterResponse.toDate}
          onChange={handleToDate}
          width="100%"
          maxDate={new Date()}
        />
      </FlexColumn>
      <FlexColumn style={{ width: "16%", display: "flex", gap: "8px" }}>
        <CustomButton use="tertiary" onClick={handleApplyButton} disabled={shouldDisableApplyButton()} style={{ flex: 1 }}>
          Apply
        </CustomButton>
        <CustomButton use="secondary" onClick={handleResetButton} disabled={shouldDisableResetButton()} style={{ flex: 1 }}>
          Reset
        </CustomButton>
      </FlexColumn>
    </Row>
  );
};

FilterBlock.propTypes = {
  setFilterResponse: PropTypes.func.isRequired,
  filterResponse: PropTypes.shape({
    fromDate: PropTypes.string,
    toDate: PropTypes.string,
    cfstFilterValue: PropTypes.string,
    selectDates: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    }),
  }).isRequired,
  getDashboardCountsWithDeRe: PropTypes.func.isRequired,
  setIsFilterReset: PropTypes.func.isRequired,
};

export default FilterBlock;
